// Texto seguro para la interfaz: escapar, plantillas que escapan solas, enlaces en texto plano y fechas.
// No depende del simulador: la usan el editor y el portal.

export function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

/** HTML ya armado (no se vuelve a escapar dentro de html``). */
class SafeHtml {
  constructor(s) {
    this.s = s;
  }
  toString() {
    return this.s;
  }
}

/** Marca como HTML un texto que NO viene del usuario (p. ej. un ícono SVG fijo). */
export function raw(s) {
  return new SafeHtml(String(s));
}

function piece(v) {
  if (v === null || v === undefined || v === false) return '';
  if (v instanceof SafeHtml) return v.s;
  if (Array.isArray(v)) return v.map(piece).join('');
  return escapeHtml(v);
}

/** Plantilla que escapa todo lo interpolado salvo lo que ya es html`` o raw(): html`<b>${nombre}</b>`. */
export function html(strings, ...values) {
  let out = strings[0];
  for (let i = 0; i < values.length; i++) out += piece(values[i]) + strings[i + 1];
  return new SafeHtml(out);
}

/**
 * Texto plano con saltos de línea respetados (el contenedor usa white-space: pre-wrap) y las URL http(s)
 * convertidas en enlaces. Todo lo demás se escapa.
 */
export function linkify(text) {
  const s = String(text ?? '');
  const re = /\bhttps?:\/\/[^\s<>"'`]+/gi;
  let out = '';
  let last = 0;
  let m;
  while ((m = re.exec(s))) {
    let url = m[0];
    // La puntuación final no es parte del enlace («ver https://x.org.»), salvo un paréntesis que cierra uno abierto.
    for (;;) {
      const c = url.slice(-1);
      if ('.,;:!?»”’\'"]}'.includes(c)) url = url.slice(0, -1);
      else if (c === ')' && (url.match(/\(/g) || []).length < (url.match(/\)/g) || []).length) url = url.slice(0, -1);
      else break;
    }
    out += escapeHtml(s.slice(last, m.index));
    out += `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(url)}</a>`;
    last = m.index + url.length;
    re.lastIndex = last;
  }
  return new SafeHtml(out + escapeHtml(s.slice(last)));
}

// Fechas: la API usa ISO 8601 en UTC; se muestran en la hora local del navegador.
const dateTime = new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
const dateOnly = new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' });
const rel = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
const num = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 1 });

function toDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDateTime(iso) {
  const d = toDate(iso);
  return d ? dateTime.format(d) : '';
}

export function fmtDate(iso) {
  const d = toDate(iso);
  return d ? dateOnly.format(d) : '';
}

/** «hace 5 minutos», «mañana», «en 3 días». */
export function fmtRelative(iso, now = Date.now()) {
  const d = toDate(iso);
  if (!d) return '';
  const s = (d.getTime() - now) / 1000;
  const a = Math.abs(s);
  if (a < 60) return 'ahora';
  if (a < 3600) return rel.format(Math.round(s / 60), 'minute');
  if (a < 86400) return rel.format(Math.round(s / 3600), 'hour');
  if (a < 30 * 86400) return rel.format(Math.round(s / 86400), 'day');
  return fmtDate(iso);
}

/** Número con un decimal como máximo (calificaciones y puntos). */
export function fmtNum(n) {
  return n === null || n === undefined || n === '' || !Number.isFinite(Number(n)) ? '' : num.format(Number(n));
}

/** ISO en UTC → valor de <input type="datetime-local"> en la hora local. */
export function toLocalInput(iso) {
  const d = toDate(iso);
  if (!d) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Valor de <input type="datetime-local"> (hora local) → ISO en UTC, o null si está vacío. */
export function fromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Iniciales para el círculo de la cuenta: «Ana López» → «AL». */
export function initials(name) {
  const words = String(name || '').split(/\s+/).map((w) => w.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean);
  if (!words.length) return '?';
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : words[0][1] || '';
  return (first + last).toUpperCase();
}
